import { PropertyValues, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { Ref, createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { SchmancyElement } from '@schmancy/mixins/index'
import { ChartMixin, POINT_RADIUS, type ChartLayout } from './chart.mixin'
import type { ChartPadding, ChartPoint, ChartSelectEvent } from './types'

/**
 * Selectable-series chart: every point is a focusable `<button>` overlay
 * (keyboard-activatable via native button semantics), emitting `select` on
 * activation. Per-point value callouts above each marker and a date-style
 * caption below.
 */
@customElement('schmancy-line-chart')
export class SchmancyLineChart extends ChartMixin(SchmancyElement) {
	/** Controlled — `-1` means "effective last". */
	@property({ type: Number }) selected = -1
	@property({ attribute: false }) ariaLabelFor?: (p: ChartPoint) => string

	override curve = 'linear' as const

	/** Every point is its own HTML dot inside its hit target. */
	override marksPoint(): boolean {
		return true
	}

	override defaultPadding(): Required<ChartPadding> {
		return { top: 24, right: 20, bottom: 40, left: 20 }
	}

	// Both rows are tabular-nums, so a row's width is a pure function of its
	// longest string — one hidden probe per row, measured once per render
	// pass rather than per label.
	@state() private labelW = 0
	@state() private captionW = 0
	private labelProbeRef: Ref<HTMLSpanElement> = createRef()
	private captionProbeRef: Ref<HTMLSpanElement> = createRef()

	protected override updated(changed: PropertyValues): void {
		super.updated(changed)
		// The probes exist only in the DOM this update just committed, so the
		// measurement is inherently post-render and the re-render it drives is
		// real. Batched behind updateComplete rather than written here: a
		// reactive write inside `updated()` schedules mid-cycle (Lit's
		// `change-in-update` warning on every mount); the batch schedules the
		// same follow-up cleanly, and settles because a re-measure of the same
		// probe writes the same value, which schedules nothing.
		this.updateComplete.then(() => {
			this.labelW = this.labelProbeRef.value?.getBoundingClientRect().width ?? 0
			this.captionW = this.captionProbeRef.value?.getBoundingClientRect().width ?? 0
		})
	}

	override renderOverlays(layout: ChartLayout) {
		const n = layout.points.length
		const effectiveSelected = this.selected === -1 ? n - 1 : this.selected

		const longestValue = layout.points.reduce((longest, p) => {
			const text = this.formatValue(p.point.value, p.point)
			return text.length > longest.length ? text : longest
		}, '')
		const longestCaption = layout.points.reduce((longest, p) => {
			const text = p.point.label ?? ''
			return text.length > longest.length ? text : longest
		}, '')

		const keptLabels = this.keptLabelIndices(layout, this.labelW, effectiveSelected)
		const keptCaptions = this.keptLabelIndices(layout, this.captionW, effectiveSelected)

		return html`
			<div class="absolute inset-0">
				${repeat(
					layout.points,
					p => p.index,
					p => {
						const isSelected = p.index === effectiveSelected
						const dotSize = POINT_RADIUS * 2
						const hitSize = Math.max(dotSize, 24)
						return html`
							<button
								type="button"
								class=${this.classMap({
									'absolute -translate-x-1/2 -translate-y-1/2 grid place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default': true,
								})}
								style=${this.styleMap({
									left: `${this.xPercent(p)}%`,
									top: `${p.y}px`,
									width: `${hitSize}px`,
									height: `${hitSize}px`,
								})}
								aria-label=${this.ariaLabelFor?.(p.point) ?? p.point.label ?? this.formatValue(p.point.value, p.point)}
								aria-pressed=${isSelected}
								@click=${() => {
									const event: ChartSelectEvent = new CustomEvent('select', {
										detail: { index: p.index, point: p.point, data: p.point.data },
										bubbles: true,
										composed: true,
									})
									this.dispatchEvent(event)
								}}
							>
								<span
									class=${this.classMap({
										'block rounded-full transition-transform hover:scale-125': true,
										// An HTML dot, so `fill` is inert here and the sibling charts'
										// FILL map paints nothing at all. It stays HTML rather than
										// joining the unstretched SVG layer because hover:scale-125 and
										// the selection ring are the affordance.
										'bg-primary-default': true,
										'ring-4 ring-primary-default/25': isSelected,
									})}
									style=${this.styleMap({ width: `${dotSize}px`, height: `${dotSize}px` })}
								></span>
							</button>
						`
					},
				)}
			</div>

			<span
				${ref(this.labelProbeRef)}
				class="invisible absolute pointer-events-none whitespace-nowrap text-[11px] font-semibold tabular-nums"
				>${longestValue}</span
			>
			<div class="absolute inset-0 pointer-events-none">
				${repeat(
					layout.points,
					p => p.index,
					p =>
						when(
							keptLabels.has(p.index),
							() => html`
								<span
									class=${this.classMap({
										'absolute -translate-y-[140%] text-[11px] font-semibold tabular-nums whitespace-nowrap': true,
										'-translate-x-1/2 text-center': this.horizontalAlignment(p) === 'center',
										'text-right': this.horizontalAlignment(p) === 'right',
										'text-surface-on': p.index === effectiveSelected || p.isLast,
										'text-surface-on-variant': p.index !== effectiveSelected && !p.isLast,
									})}
									style=${this.styleMap({ ...this.horizontalAnchorStyle(p), top: `${p.y}px` })}
									>${this.formatValue(p.point.value, p.point)}</span
								>
							`,
						),
				)}
			</div>

			<span
				${ref(this.captionProbeRef)}
				class="invisible absolute pointer-events-none whitespace-nowrap text-[10px] tabular-nums"
				>${longestCaption}</span
			>
			<div class="absolute inset-x-0 bottom-0.5 h-4 pointer-events-none">
				${repeat(
					layout.points,
					p => p.index,
					p =>
						when(
							keptCaptions.has(p.index),
							() => html`
								<span
									class=${this.classMap({
										'absolute text-[10px] text-surface-on-variant opacity-60 whitespace-nowrap tabular-nums': true,
										'-translate-x-1/2': this.horizontalAlignment(p) === 'center',
										'text-right': this.horizontalAlignment(p) === 'right',
									})}
									style=${this.styleMap(this.horizontalAnchorStyle(p))}
									>${p.point.label ?? ''}</span
								>
							`,
						),
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-line-chart': SchmancyLineChart
	}
}
